import { z } from "zod";

export const PhoneCallDirectionSchema = z.enum(["INBOUND", "OUTBOUND"]);

export const PhoneCallOutcomeSchema = z.enum([
  "COMPLETED",
  "PARTIALLY_COMPLETED",
  "MISSED",
  "NO_ANSWER",
  "BUSY",
  "DECLINED",
  "VOICEMAIL",
  "ABANDONED",
  "CANCELED",
  "FAILED",
]);

export const PhoneCallParticipantKindSchema = z.enum([
  "CONTACT",
  "USER",
  "FIRM_PHONE",
  "EXTERNAL",
]);

export const PhoneCallMethodSchema = z.enum(["MANUAL", "VOIP"]);

const rawIdSchema = z
  .string()
  .trim()
  .min(1)
  .regex(/^[^#:]+$/);
const requiredTextSchema = z.string().trim().min(1);
const nullableTimestampSchema = z.string().nullable();
const participantIntervalSchema = {
  joinedAt: z.string().nullable().optional(),
  leftAt: z.string().nullable().optional(),
};

const validateParticipantInterval = (
  participant: { joinedAt?: string | null; leftAt?: string | null },
  context: z.RefinementCtx,
) => {
  if (
    participant.joinedAt &&
    participant.leftAt &&
    new Date(participant.leftAt) < new Date(participant.joinedAt)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "leftAt cannot be before joinedAt",
      path: ["leftAt"],
    });
  }
};

const ContactPhoneCallContactParticipantInputSchema = z
  .object({
    ...participantIntervalSchema,
    kind: z.literal("CONTACT"),
    contactId: rawIdSchema,
    phoneNumber: requiredTextSchema.nullable().optional(),
  })
  .strict();

const ContactPhoneCallUserParticipantInputSchema = z
  .object({
    ...participantIntervalSchema,
    kind: z.literal("USER"),
    userId: rawIdSchema,
    phoneNumber: requiredTextSchema.nullable().optional(),
  })
  .strict();

const ContactPhoneCallFirmPhoneParticipantInputSchema = z
  .object({
    ...participantIntervalSchema,
    kind: z.literal("FIRM_PHONE"),
    firmPhoneId: rawIdSchema.nullable().optional(),
    phoneNumber: requiredTextSchema.nullable().optional(),
  })
  .strict()
  .refine(
    ({ firmPhoneId, phoneNumber }) => Boolean(firmPhoneId || phoneNumber),
    {
      message: "FIRM_PHONE participants require firmPhoneId or phoneNumber",
      path: ["firmPhoneId"],
    },
  );

const ContactPhoneCallExternalParticipantInputSchema = z
  .object({
    ...participantIntervalSchema,
    kind: z.literal("EXTERNAL"),
    phoneNumber: requiredTextSchema,
  })
  .strict();

export const ContactPhoneCallParticipantInputSchema = z
  .union([
    ContactPhoneCallContactParticipantInputSchema,
    ContactPhoneCallUserParticipantInputSchema,
    ContactPhoneCallFirmPhoneParticipantInputSchema,
    ContactPhoneCallExternalParticipantInputSchema,
  ])
  .superRefine(validateParticipantInterval);

const validateCallInterval = (
  call: {
    startedAt?: string;
    endedAt?: string;
    participants?: Array<{ joinedAt?: string | null; leftAt?: string | null }>;
  },
  context: z.RefinementCtx,
) => {
  if (!call.startedAt || !call.endedAt) return;

  const startedAt = new Date(call.startedAt);
  const endedAt = new Date(call.endedAt);
  if (endedAt < startedAt) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "endedAt cannot be before startedAt",
      path: ["endedAt"],
    });
  }

  call.participants?.forEach((participant, index) => {
    (["joinedAt", "leftAt"] as const).forEach((field) => {
      const value = participant[field];
      if (value && (new Date(value) < startedAt || new Date(value) > endedAt)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Participant ${field} must fall within the call interval`,
          path: ["participants", index, field],
        });
      }
    });
  });
};

export const ContactPhoneCallCreateInputSchema = z
  .object({
    direction: PhoneCallDirectionSchema,
    startedAt: z.string().min(1),
    endedAt: z.string().min(1),
    description: z.string().nullable().optional(),
    outcome: PhoneCallOutcomeSchema,
    participants: z.array(ContactPhoneCallParticipantInputSchema).min(2),
  })
  .strict()
  .superRefine(validateCallInterval);

export const ContactPhoneCallUpdateInputSchema = z
  .object({
    dateModified: z.string().min(1),
    direction: PhoneCallDirectionSchema.optional(),
    startedAt: z.string().min(1).optional(),
    endedAt: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    outcome: PhoneCallOutcomeSchema.optional(),
    participants: z
      .array(ContactPhoneCallParticipantInputSchema)
      .min(2)
      .optional(),
  })
  .strict()
  .refine(
    ({ direction, startedAt, endedAt, description, outcome, participants }) =>
      direction !== undefined ||
      startedAt !== undefined ||
      endedAt !== undefined ||
      description !== undefined ||
      outcome !== undefined ||
      participants !== undefined,
    { message: "At least one phone call field must be provided" },
  )
  .superRefine(validateCallInterval);

export const ContactPhoneCallParticipantSchema = z.object({
  id: z.string(),
  firmId: z.string(),
  phoneCallId: z.string(),
  kind: PhoneCallParticipantKindSchema,
  contactId: z.string().nullable(),
  userId: z.string().nullable(),
  firmPhoneId: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  joinedAt: z.string().nullable(),
  leftAt: z.string().nullable(),
  dateCreated: nullableTimestampSchema,
  dateModified: nullableTimestampSchema,
});

export const ContactPhoneCallSchema = z.object({
  id: z.string(),
  firmId: z.string(),
  direction: PhoneCallDirectionSchema,
  startedAt: z.string(),
  endedAt: z.string(),
  description: z.string().nullable(),
  outcome: PhoneCallOutcomeSchema,
  method: PhoneCallMethodSchema,
  participants: z.array(ContactPhoneCallParticipantSchema),
  dateCreated: nullableTimestampSchema,
  dateModified: nullableTimestampSchema,
});
